import React, { useRef, useLayoutEffect, useState } from "react";

interface ProgressIndicatorProps {
  stepLabels: string[];
  formStep: number;
  handleStepClick: (stepIndex: number) => void;
  stepsCompleted: boolean[];
}

export default function ProgressIndicator({ stepLabels, formStep, handleStepClick, stepsCompleted }: ProgressIndicatorProps) {
  // --- MOBILE LABEL MEASUREMENT ---
  const labelRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [labelWidths, setLabelWidths] = useState<number[]>([]);

  useLayoutEffect(() => {
    const widths = labelRefs.current.map((el) => el?.offsetWidth || 0);

    setLabelWidths(widths);
  }, [stepLabels]);

  const totalWidth = labelWidths.reduce((a, b) => a + b, 0) || 1;
  const flexValues = labelWidths.map((w) => w / totalWidth);
  const useDynamicSpacing = stepLabels.length > 4;

  return (
    <>
      {/* DESKTOP VERSION */}
      <div className="hidden md:block w-4/5 mx-auto mb-1 mt-2 relative">
        {/* Connector segments */}
        <div className="absolute top-[33%] left-[calc(theme(spacing.5)+20px)] right-5 flex overflow-hidden">
          {[...Array(stepLabels.length - 1)].map((_, i) => (
            <div key={i} className={`flex-1 ${i < formStep ? "bg-[#d8bd80] h-[3px]" : "bg-gray-300 h-[2px]"}`} />
          ))}
        </div>

        {/* Circles + labels */}
        <div className="flex items-center justify-between gap-x-12 relative">
          {stepLabels.map((label, i) => {
            const isCurrent = i === formStep;
            const isCompleted = i < formStep;

            return (
              <div key={i} className="flex flex-col items-center flex-shrink-0">
                <div className="w-10 h-10 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => handleStepClick(i)}
                    disabled={i > formStep + 1 && !stepsCompleted[i - 1]}
                    className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium
                      ${
                        isCurrent
                          ? "bg-[#d8bd80] text-white border-[#d8bd80] translate-y-[2px]"
                          : isCompleted
                            ? "bg-white text-[#d8bd80] border-4 border-[#d8bd80]"
                            : "bg-white text-gray-600 border-gray-300"
                      }`}
                  >
                    {i + 1}
                  </button>
                </div>
                <span
                  className={`mt-2 text-center min-w-[80px]
                    ${isCurrent ? "text-base font-bold text-gray-900" : "text-xs font-medium text-gray-700"}`}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* MOBILE VERSION */}
      <div className="md:hidden w-full px-1 mt-3 mb-2">
        {/* Circles row */}
        <div className="relative flex items-center h-10">
          {/* Connector line */}
          <div
            className="absolute top-[19px]"
            style={{
              left: useDynamicSpacing ? "1.5rem" : "2.9rem",
              right: useDynamicSpacing ? "1.5rem" : "2.9rem",
            }}
          >
            <div className="w-full h-[2px] bg-gray-300" />
          </div>

          {stepLabels.map((label, i) => {
            const isCurrent = i === formStep;
            const isCompleted = i < formStep;

            return (
              <div
                key={i}
                className="flex flex-col items-center z-10"
                style={useDynamicSpacing ? { flex: flexValues[i] || 1 } : { flex: 1 }}
              >
                <button
                  type="button"
                  onClick={() => handleStepClick(i)}
                  disabled={i > formStep + 1 && !stepsCompleted[i - 1]}
                  className={`
              w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium
              ${
                isCurrent
                  ? "bg-[#d8bd80] text-white border-[#d8bd80]"
                  : isCompleted
                    ? "bg-white text-[#d8bd80] border-[#d8bd80]"
                    : "bg-white text-gray-600 border-gray-300"
              }
            `}
                >
                  {i + 1}
                </button>
              </div>
            );
          })}
        </div>

        {/* Labels row */}
        <div className="flex mt-0 mb-4 gap-2">
          {stepLabels.map((label, i) => (
            <span
              key={i}
              ref={(el) => (labelRefs.current[i] = el)}
              className={`
    inline-block text-[12px] font-medium text-gray-700 leading-tight
    ${useDynamicSpacing ? "" : "text-center min-w-[60px]"}
  `}
              style={useDynamicSpacing ? { flex: flexValues[i] } : { flex: 1 }}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
